import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Stethoscope, ShieldCheck, ClipboardCheck, UserRoundCheck, HeartPulse } from "lucide-react";
const values = [{
  icon: Stethoscope,
  title: "Physician-Led TRT Care",
  description: "Your care is reviewed and guided by Dr. Flores, not a sales rep or automated protocol."
}, {
  icon: ClipboardCheck,
  title: "Labs First. Treatment When Appropriate.",
  description: "We evaluate symptoms, medical history, and lab results before deciding whether testosterone therapy makes sense."
}, {
  icon: UserRoundCheck,
  title: "Built for Busy Texas Men",
  description: "Basecamp TRT is designed for men balancing work, family, health, and performance who want clear medical guidance."
}, {
  icon: HeartPulse,
  title: "Ongoing Monitoring and Adjustments",
  description: "TRT is not a one-time prescription. We monitor safety markers, symptoms, and response over time."
}, {
  icon: ShieldCheck,
  title: "Clear, Responsible Medical Care",
  description: "No gimmicks. No exaggerated promises. Just physician-led care focused on safety, clarity, and long-term health."
}];
export default function AboutIronPeakMensHealth() {
  return <section className="pt-10 sm:pt-12 pb-10 bg-background">
      <div className="container">
        {/* Intro */}
        <div className="text-center mb-12 max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary-light-bg px-4 py-2 mb-5">
            <ShieldCheck className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary">
              Built for Men Who Don’t Want to Guess Anymore
            </span>
          </div>

          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-5 leading-snug">
            Physician-Led Testosterone Care for Men Who Want Clear Answers
          </h2>

          <p className="text-base md:text-lg text-muted-dark max-w-3xl mx-auto leading-relaxed">
            IronPeak Men’s Health helps Texas men determine whether low
            testosterone may be contributing to fatigue, low drive, weight gain,
            poor recovery, or declining performance. Our core offer is{" "}
            <strong>Basecamp TRT</strong> — a focused, physician-led testosterone
            care package built around proper lab review, medical judgment, and
            ongoing support.
          </p>
        </div>

        {/* Mission + Approach */}
        <div className="grid md:grid-cols-2 gap-8 mb-14">
          <Card className="border border-border shadow-soft bg-primary-light-bg">
            <CardHeader>
              <CardTitle className="text-2xl text-primary font-medium">
                Our Mission
              </CardTitle>
            </CardHeader>

            <CardContent>
              <p className="text-foreground leading-relaxed mb-4">
                Our mission is to give men a clear, medically responsible path
                to evaluate and treat low testosterone when clinically
                appropriate.
              </p>

              <p className="text-foreground leading-relaxed">
                We built Basecamp TRT for men who feel like they are running on
                empty and want direct physician guidance instead of guessing,
                self-managing, or bouncing between generic wellness advice.
              </p>
            </CardContent>
          </Card>

          <Card className="border border-border shadow-soft bg-secondary-light-bg">
            <CardHeader>
              <CardTitle className="text-2xl text-secondary font-medium">
                Our Approach
              </CardTitle>
            </CardHeader>

            <CardContent>
              <p className="text-foreground leading-relaxed mb-4">
                We start with the foundation: symptoms, labs, history, risk
                factors, and goals. If TRT is appropriate, we create a
                personalized plan and monitor your response over time.
              </p>

              <p className="text-foreground leading-relaxed">
                Add-on services like medical weight loss, NAD+, vitamin
                injections, advanced labs, and recovery support may be used when
                they fit your clinical picture — but they are not the starting
                point. The starting point is clarity.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Values */}
        <div className="text-center mb-10">
          <h3 className="text-2xl md:text-3xl font-medium text-foreground mb-4">
            What Makes IronPeak Different
          </h3>

          <p className="text-muted-dark max-w-3xl mx-auto leading-relaxed">
            We are not trying to be everything to everyone. We focus on
            physician-led testosterone care for men who want answers, safety,
            and a plan that makes sense.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
          {values.map((value, i) => {
          const IconComponent = value.icon;
          return <Card key={i} data-index={i} className="border border-border shadow-soft bg-card hover-lift">
                <CardHeader>
                  <div className="w-12 h-12 bg-primary-light-bg rounded-lg flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-primary" />
                  </div>

                  <CardTitle className="text-xl font-medium text-foreground">
                    {value.title}
                  </CardTitle>
                </CardHeader>

                <CardContent>
                  <p className="text-muted-dark leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Physician */}
        <div className="text-center mb-10">
          <h3 className="text-2xl md:text-3xl font-medium text-foreground mb-4">
            Meet Your Physician
          </h3>

          <p className="text-muted-dark max-w-2xl mx-auto leading-relaxed">
            At IronPeak, you work directly with a physician who understands the
            pressure of staying healthy, strong, and present while balancing
            work and family life.
          </p>
        </div>

        <div className="grid md:grid-cols-1 max-w-xl mx-auto gap-8">
          <Card className="border border-border shadow-soft hover-lift overflow-hidden">
            <img src="https://relaxed-fowl.10web.cloud/wp-content/uploads/2025/09/Michael-Flores-scaled.jpg" alt="Dr. Michael Flores, D.O." className="w-full h-64 object-cover object-[center_10%]" />

            <CardHeader>
              <CardTitle className="text-xl font-medium text-foreground">
                Dr. Michael Flores, D.O.
              </CardTitle>

              <p className="text-primary font-medium">
                Board-Certified Physician | Founder, IronPeak Men’s Health
              </p>
            </CardHeader>

            <CardContent>
              <p className="text-muted-dark leading-relaxed mb-4">
                I am a board-certified physician with over 10 years of medical
                experience. As a husband and father of three, I understand how
                important it is for men to stay healthy, capable, and present
                for the people who depend on them.
              </p>

              <p className="text-muted-dark leading-relaxed">
                My goal is to provide honest, evidence-based care for men who
                want to understand what is happening beneath the surface and
                take the right next step — whether that includes testosterone
                therapy or another medical approach.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}